import { CalendarOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  DatePicker,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Spin,
  Table,
  Tag,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import {
  MaturityScheduleResponse,
  MaturityScheduleWithTransactionInfo,
} from '../../model/MaturitySchedule.model';

import MaturityScheduleService from '../../service/maturity-schedule-service/MaturitySchedule.service';
import CardMultiExportModal from './CardMultiExportModal';

// Import existing modals and utilities
import useEmployee from '../../hook/useEmployee';
import { useIsAdmin } from '../../hook/useRole';
import DepositModal from '../maturity-schedule/DepositModal';
import EditMaturityScheduleModal from '../maturity-schedule/EditMaturityScheduleModal';
import MaturityPaymentModal from '../maturity-schedule/MaturityPaymentModal';
import {
  formatCurrency,
  getStatusColor,
  getStatusText,
} from '../maturity-schedule/MaturityTableUtils';
import TransactionSubTable from '../maturity-schedule/TransactionSubTable';
import WithdrawalModal from '../maturity-schedule/WithdrawalModal';
import MaturityScheduleExpandedActions from './MaturityScheduleExpandedActions';

interface MaturityScheduleListProps {
  cardId: number;
  onSchedulesUpdate?: (
    schedules: MaturityScheduleResponse[],
    month: number,
    year: number
  ) => void;
  cardInfo?: {
    cardNumber: string;
    cardHolderName: string;
    customerName: string;
    bankName: string;
    cardType: string;
    newStatus?: string;
  };
  initialFilters?: {
    maturityScheduleId?: number;
    year?: number;
  };
  selectedYear?: number;
  onYearChange?: (year: number) => void;
}

const MaturityScheduleList: React.FC<MaturityScheduleListProps> = ({
  cardId,
  onSchedulesUpdate,
  cardInfo,
  initialFilters,
  selectedYear: propsSelectedYear,
  onYearChange,
}) => {
  const isAdmin = useIsAdmin();
  const [schedules, setSchedules] = useState<MaturityScheduleResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(
    propsSelectedYear || initialFilters?.year || dayjs().year()
  );
  const [tableKey, setTableKey] = useState<number>(0);
  const [autoFilterApplied, setAutoFilterApplied] = useState(false);
  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);
  const [showMultiExportModal, setShowMultiExportModal] = useState(false);
  const [transactionRefreshTrigger, setTransactionRefreshTrigger] = useState(0);

  // Modal states for reusing existing modals
  const [depositModalVisible, setDepositModalVisible] = useState(false);
  const [withdrawalModalVisible, setWithdrawalModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] =
    useState<MaturityScheduleResponse | null>(null);

  // Employee hook for actions
  const { employee } = useEmployee();

  // Create employee names object from the enhanced data
  const employeeNames = React.useMemo(() => {
    const names: { [key: number]: string } = {};
    schedules.forEach((schedule) => {
      schedule.employees?.forEach((employee) => {
        names[employee.id] = employee.name;
      });
      // Add creator name
      if (schedule.createdBy && schedule.createdByName) {
        names[schedule.createdBy] = schedule.createdByName;
      }
    });
    return names;
  }, [schedules]);

  // Create unique employee options for filters
  const uniqueEmployees = React.useMemo(() => {
    const employeeSet = new Set<string>();
    schedules.forEach((schedule) => {
      schedule.employees?.forEach((employee) => {
        const name = employeeNames[employee.id] || employee.name;
        if (name) employeeSet.add(name);
      });
    });
    return Array.from(employeeSet).map((name) => ({
      label: name,
      value: name,
    }));
  }, [schedules, employeeNames]);

  // Create unique creator options for filters
  const uniqueCreators = React.useMemo(() => {
    const creatorSet = new Set<string>();
    schedules.forEach((schedule) => {
      if (schedule.createdBy && schedule.createdByName) {
        creatorSet.add(schedule.createdByName);
      }
    });
    return Array.from(creatorSet).map((name) => ({
      label: name,
      value: name,
    }));
  }, [schedules]);

  useEffect(() => {
    loadSchedules(); // Load data when cardId or selectedYear changes
  }, [cardId, selectedYear]);

  // Auto-filter effect for URL params
  useEffect(() => {
    if (
      initialFilters?.maturityScheduleId &&
      schedules.length > 0 &&
      !autoFilterApplied
    ) {
      // Auto-filter the table by ID
      const targetSchedule = schedules.find(
        (schedule) => schedule.id === initialFilters.maturityScheduleId
      );

      if (targetSchedule) {
        console.log('Auto-filtering to maturity schedule:', targetSchedule.id);

        // Set filter info to auto-filter by ID
        setFilteredInfo({
          id: [targetSchedule.id.toString()],
        });

        // Auto-expand the row to show transactions
        setExpandedRowKeys([targetSchedule.id]);

        setAutoFilterApplied(true);
      }
    } else if (!initialFilters?.maturityScheduleId && autoFilterApplied) {
      // If initialFilters is cleared and we had previously auto-filtered,
      // reset everything
      setFilteredInfo({});
      setExpandedRowKeys([]);
      setAutoFilterApplied(false);
    }
  }, [schedules, initialFilters?.maturityScheduleId, autoFilterApplied]);

  const loadSchedules = async () => {
    try {
      setLoading(true);

      const data =
        await MaturityScheduleService.getAllMaturityScheduleByYearAndCard(
          selectedYear,
          cardId
        );

      // Convert the DTO data to the expected format
      const convertedData = data.map(
        (dto: MaturityScheduleWithTransactionInfo) => {
          // Use DTO creditCard if available, otherwise create basic fallback from cardInfo
          const creditCard =
            dto.creditCard ||
            ({
              id: cardId,
              cardNumber: cardInfo?.cardNumber || '',
              cardHolderName: cardInfo?.cardHolderName || '',
            } as any);

          // Debug log to see DTO structure
          console.log('DTO received for MaturitySchedule ID:', dto.id);
          console.log(
            'DTO commissionRateForPartner:',
            dto.commissionRateForPartner
          );
          console.log('DTO employees:', dto.employees);

          return {
            id: dto.id,
            createdAt: '', // Not provided by DTO, but not critical for display
            updatedAt: '', // Not provided by DTO, but not critical for display
            deletedAt: null,
            dueDate: dto.dueDate,
            repaymentAmount: dto.repaymentAmount,
            status: dto.status,
            feeApplied: dto.feeApplied,
            creditCard: creditCard,
            transactions: [], // Not provided by DTO since we have calculated totals
            employees: dto.employees || [],
            notes: dto.notes,
            isChecked: false, // Default value
            commissionRateForPartner: dto.commissionRateForPartner || 0, // Use DTO field directly
            createdBy: dto.createdBy,
            createdByName: dto.createdByName,
            totalDepositAmount: dto.totalDepositAmount,
            totalWithdrawalAmount: dto.totalWithdrawalAmount,
            remainingAmount: dto.remainingAmount,
            balanceDifference: dto.balanceDifference,
            totalPaymentAmount: dto.totalPaymentAmount,
          };
        }
      );

      setSchedules(convertedData);

      // Notify parent component about schedules update
      if (onSchedulesUpdate) {
        onSchedulesUpdate(convertedData, 0, selectedYear); // month = 0 để indicate cả năm
      }
    } catch (error) {
      message.error('Không thể tải danh sách kỳ đáo hạn');
      console.error('Error loading maturity schedules:', error);
    } finally {
      setLoading(false);
    }
  };

  // Clear all filters
  const handleClearAllFilters = () => {
    setTableKey((prev) => prev + 1);
    setFilteredInfo({});
    setAutoFilterApplied(false);
    setExpandedRowKeys([]);

    // Reset initialFilters to prevent auto-filtering again
    if (initialFilters?.maturityScheduleId) {
      initialFilters.maturityScheduleId = undefined;
    }
  };

  // Handle expand/collapse
  const handleExpand = (
    expanded: boolean,
    record: MaturityScheduleResponse
  ) => {
    if (expanded) {
      setExpandedRowKeys([...expandedRowKeys, record.id]);
    } else {
      setExpandedRowKeys(expandedRowKeys.filter((key) => key !== record.id));
    }
  };

  // Check if row is expanded
  const isRowExpanded = (recordId: number) => {
    return expandedRowKeys.includes(recordId);
  };

  // Action handlers
  const handleViewDetails = (record: MaturityScheduleResponse) => {
    Modal.info({
      title: `Chi tiết kỳ đáo hạn #${record.id}`,
      content: (
        <div className="space-y-3">
          <div>
            <strong>Ngày đáo hạn:</strong>{' '}
            {dayjs(record.dueDate).format('DD/MM/YYYY')}
          </div>
          <div>
            <strong>Số tiền:</strong> {formatCurrency(record.repaymentAmount)}
          </div>
          <div>
            <strong>Phí thu:</strong>{' '}
            {record.feeApplied ? `${record.feeApplied.toFixed(2)}%` : '-'}
          </div>
          <div>
            <strong>Còn phải nạp:</strong>{' '}
            {formatCurrency(record.remainingAmount || 0)}
          </div>
          <div>
            <strong>Đã nạp:</strong>{' '}
            {formatCurrency(record.totalDepositAmount || 0)}
          </div>
          <div>
            <strong>Đã rút:</strong>{' '}
            {formatCurrency(record.totalWithdrawalAmount || 0)}
          </div>
          <div>
            <strong>Thanh toán:</strong>{' '}
            {formatCurrency(record.totalPaymentAmount || 0)}
          </div>
          <div>
            <strong>Trạng thái:</strong> {getStatusText(record.status)}
          </div>
          <div>
            <strong>Ghi chú:</strong> {record.notes || '-'}
          </div>
        </div>
      ),
      width: 600,
    });
  };

  const handleEdit = (record: MaturityScheduleResponse) => {
    if (!employee?.id) {
      message.error('Không tìm thấy thông tin nhân viên');
      return;
    }
    setSelectedRecord(record);
    setEditModalVisible(true);
  };

  const handleDelete = (record: MaturityScheduleResponse) => {
    if (!employee?.id) {
      message.error('Không tìm thấy thông tin nhân viên');
      return;
    }

    Modal.confirm({
      title: 'Xác nhận xóa',
      content: `Bạn có chắc chắn muốn xóa kỳ đáo hạn #${record.id}?`,
      okText: 'Xóa',
      cancelText: 'Hủy',
      okType: 'danger',
      onOk: async () => {
        try {
          setLoading(true); // Show loading during delete
          await MaturityScheduleService.deleteMaturitySchedule(
            record.id,
            employee.id
          );
          message.success(`Đã xóa kỳ đáo hạn #${record.id}`);
          await loadSchedules(); // Refresh data and notify parent
        } catch (error) {
          console.error('Error deleting maturity schedule:', error);
          message.error('Không thể xóa kỳ đáo hạn');
        } finally {
          setLoading(false); // Hide loading
        }
      },
    });
  };

  // New action handlers using existing modals
  const handleDeposit = (record: MaturityScheduleResponse) => {
    setSelectedRecord(record);
    setDepositModalVisible(true);
  };

  const handleWithdrawal = (record: MaturityScheduleResponse) => {
    setSelectedRecord(record);
    setWithdrawalModalVisible(true);
  };

  const handleToggleCompletion = async (record: MaturityScheduleResponse) => {
    if (!employee?.id) {
      message.error('Không tìm thấy thông tin nhân viên');
      return;
    }

    try {
      setLoading(true);
      await MaturityScheduleService.toggleCompletionStatus(
        record.id,
        employee.id
      );
      const action = record.status === 'COMPLETED' ? 'bỏ đánh dấu' : 'đánh dấu';
      message.success(`Đã ${action} hoàn thành kỳ đáo hạn #${record.id}`);
      await loadSchedules(); // Refresh data and notify parent
    } catch (error) {
      console.error('Error toggling completion status:', error);
      message.error('Không thể cập nhật trạng thái hoàn thành');
    } finally {
      setLoading(false);
    }
  };

  const handleRecordPayment = (record: MaturityScheduleResponse) => {
    setSelectedRecord(record);
    setPaymentModalVisible(true);
  };

  const handleExportExcel = async (record: MaturityScheduleResponse) => {
    try {
      // Enhance record with cardInfo if needed
      const enhancedRecord = {
        ...record,
        creditCard: {
          ...record.creditCard,
          cardNumber:
            record.creditCard?.cardNumber || cardInfo?.cardNumber || 'N/A',
          cardHolderName:
            record.creditCard?.cardHolderName ||
            cardInfo?.cardHolderName ||
            'N/A',
          bank: {
            ...record.creditCard?.bank,
            name: record.creditCard?.bank?.name || cardInfo?.bankName || 'N/A',
          },
          cardType: {
            ...record.creditCard?.cardType,
            name:
              record.creditCard?.cardType?.name || cardInfo?.cardType || 'N/A',
          },
          customer: {
            ...record.creditCard?.customer,
            name:
              record.creditCard?.customer?.name ||
              cardInfo?.customerName ||
              'N/A',
          },
        },
      };

      // Import and use the SingleMaturityExportService
      const { SingleMaturityExportService } = await import(
        '../../utils/excel/singleMaturityExportService'
      );

      await SingleMaturityExportService.exportToExcel(enhancedRecord);
      message.success(
        `Đã xuất hóa đơn cho kỳ đáo hạn #${record.id} thành công!`
      );
    } catch (error) {
      console.error('Error exporting invoice:', error);
      message.error('Có lỗi xảy ra khi xuất hóa đơn');
    }
  };

  // Modal success handlers
  const handleModalSuccess = () => {
    loadSchedules(); // Refresh data after any modal action
    setTransactionRefreshTrigger((prev) => prev + 1); // Trigger transaction refresh
  };

  // Optimized refresh function for actions that affect data
  const refreshData = async () => {
    try {
      await loadSchedules();
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  };

  const handleModalClose = (
    modalSetter: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    modalSetter(false);
    setSelectedRecord(null);
  };

  // Helper function for payment amount (referenced in utility functions)
  const getPaymentAmount = (maturityScheduleId: number): number => {
    // This could be enhanced to fetch from a payment service if needed
    // For now, return 0 as placeholder
    return 0;
  };

  const columns: ColumnsType<MaturityScheduleResponse> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      fixed: 'left',
      render: (id: number) => (
        <span className="font-mono text-sm text-gray-600">#{id}</span>
      ),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div className="p-2">
          <InputNumber
            placeholder="Tìm ID"
            value={selectedKeys[0] as number}
            onChange={(value) => setSelectedKeys(value ? [value] : [])}
            onPressEnter={() => confirm()}
            style={{ width: 120, marginBottom: 8, display: 'block' }}
          />
          <div className="flex gap-1">
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 60 }}
            >
              Tìm
            </Button>
            <Button
              onClick={() => clearFilters?.()}
              size="small"
              style={{ width: 60 }}
            >
              Xóa
            </Button>
          </div>
        </div>
      ),
      filteredValue: filteredInfo.id || null,
      onFilter: (value, record) =>
        record.id.toString().includes(value.toString()),
    },
    {
      title: 'Ngày thanh toán',
      key: 'cyclePaymentDate',
      width: 120,
      render: (_, record) => {
        const cyclePaymentDate = record.creditCard?.cyclePaymentDate;
        return cyclePaymentDate ? cyclePaymentDate : '-';
      },
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div className="p-2">
          <Select
            mode="multiple"
            placeholder="Chọn ngày thanh toán"
            value={selectedKeys as number[]}
            onChange={(value) => setSelectedKeys(value)}
            style={{ width: 200, marginBottom: 8 }}
            options={Array.from({ length: 31 }, (_, i) => ({
              label: `${i + 1}`,
              value: i + 1,
            }))}
            allowClear
          />
          <div className="flex gap-1">
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 60 }}
            >
              Lọc
            </Button>
            <Button
              onClick={() => clearFilters?.()}
              size="small"
              style={{ width: 60 }}
            >
              Xóa
            </Button>
          </div>
        </div>
      ),
      filteredValue: filteredInfo.cyclePaymentDate || null,
      onFilter: (value, record) => {
        if (!value || (Array.isArray(value) && value.length === 0)) return true;
        const selectedDays = Array.isArray(value) ? value : [value];
        const cyclePaymentDate = record.creditCard?.cyclePaymentDate;
        if (typeof cyclePaymentDate !== 'number') return false;
        return selectedDays.includes(cyclePaymentDate);
      },
    },
    {
      title: 'Hạn đáo',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 120,
      render: (date: string) => dayjs(date).format('DD-MM-YYYY'),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div className="p-2">
          <DatePicker
            placeholder="Chọn ngày đáo hạn"
            value={selectedKeys[0] ? dayjs(selectedKeys[0] as string) : null}
            onChange={(date: dayjs.Dayjs | null) => {
              if (date) {
                setSelectedKeys([date.format('YYYY-MM-DD')]);
              } else {
                setSelectedKeys([]);
              }
            }}
            style={{ width: 160, marginBottom: 8, display: 'block' }}
            allowClear
          />
          <div className="flex gap-1">
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 60 }}
            >
              Lọc
            </Button>
            <Button
              onClick={() => clearFilters?.()}
              size="small"
              style={{ width: 60 }}
            >
              Xóa
            </Button>
          </div>
        </div>
      ),
      filteredValue: filteredInfo.dueDate || null,
      onFilter: (value, record) => {
        if (!value) return true;
        try {
          const filterDate = dayjs(value as string);
          const recordDate = dayjs(record.dueDate);
          return recordDate.isSame(filterDate, 'day');
        } catch (error) {
          console.error('Date filter error:', error);
          return true;
        }
      },
    },
    {
      title: 'Số tiền đáo hạn',
      dataIndex: 'repaymentAmount',
      key: 'repaymentAmount',
      width: 150,
      render: (amount: number) => (
        <span className="font-semibold text-blue-600">
          {formatCurrency(amount)}
        </span>
      ),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div className="p-2">
          <InputNumber
            placeholder="Nhập số tiền"
            value={selectedKeys[0] as number}
            onChange={(value) => setSelectedKeys(value ? [value] : [])}
            onPressEnter={() => confirm()}
            style={{ width: 150, marginBottom: 8, display: 'block' }}
            formatter={(value) =>
              value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''
            }
            parser={(value) => {
              if (!value) return 0;
              const parsed = parseFloat(value.replace(/\$\s?|(,*)/g, ''));
              return isNaN(parsed) ? 0 : parsed;
            }}
          />
          <div className="flex gap-1">
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 60 }}
            >
              Lọc
            </Button>
            <Button
              onClick={() => clearFilters?.()}
              size="small"
              style={{ width: 60 }}
            >
              Xóa
            </Button>
          </div>
        </div>
      ),
      filteredValue: filteredInfo.repaymentAmount || null,
      onFilter: (value, record) => {
        if (!value) return true;
        return record.repaymentAmount === value;
      },
    },
    {
      title: 'Phí thu (%)',
      dataIndex: 'feeApplied',
      key: 'feeApplied',
      width: 120,
      render: (fee: number) => {
        if (!fee && fee !== 0) return '-';
        return `${fee.toFixed(2)}%`;
      },
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div className="p-2">
          <InputNumber
            placeholder="Nhập phí (%)"
            value={selectedKeys[0] as number}
            onChange={(value) => setSelectedKeys(value ? [value] : [])}
            onPressEnter={() => confirm()}
            style={{ width: 120, marginBottom: 8, display: 'block' }}
            step={0.1}
            precision={2}
            min={0}
            max={100}
          />
          <div className="flex gap-1">
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 60 }}
            >
              Lọc
            </Button>
            <Button
              onClick={() => clearFilters?.()}
              size="small"
              style={{ width: 60 }}
            >
              Xóa
            </Button>
          </div>
        </div>
      ),
      filteredValue: filteredInfo.feeApplied || null,
      onFilter: (value, record) => {
        if (!value) return true;
        const fee = record.feeApplied || 0;
        return fee === value;
      },
    },
    {
      title: 'Số tiền còn phải nạp',
      key: 'remainingAmount',
      width: 160,
      render: (_, record) => {
        const amount = record.remainingAmount || 0;
        return (
          <span
            className={`font-semibold ${amount > 0 ? 'text-orange-600' : 'text-gray-500'}`}
          >
            {formatCurrency(amount)}
          </span>
        );
      },
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div className="p-2">
          <InputNumber
            placeholder="Nhập số tiền"
            value={selectedKeys[0] as number}
            onChange={(value) => setSelectedKeys(value ? [value] : [])}
            onPressEnter={() => confirm()}
            style={{ width: 150, marginBottom: 8, display: 'block' }}
            formatter={(value) =>
              value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''
            }
            parser={(value) => {
              if (!value) return 0;
              const parsed = parseFloat(value.replace(/\$\s?|(,*)/g, ''));
              return isNaN(parsed) ? 0 : parsed;
            }}
          />
          <div className="flex gap-1">
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 60 }}
            >
              Lọc
            </Button>
            <Button
              onClick={() => clearFilters?.()}
              size="small"
              style={{ width: 60 }}
            >
              Xóa
            </Button>
          </div>
        </div>
      ),
      filteredValue: filteredInfo.remainingAmount || null,
      onFilter: (value, record) => {
        if (!value) return true;
        return (record.remainingAmount || 0) === value;
      },
    },
    {
      title: 'Đã nạp',
      key: 'totalDeposit',
      width: 130,
      render: (_, record) => {
        const amount = record.totalDepositAmount || 0;
        return <span className="font-semibold ">{formatCurrency(amount)}</span>;
      },
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div className="p-2">
          <InputNumber
            placeholder="Nhập số tiền"
            value={selectedKeys[0] as number}
            onChange={(value) => setSelectedKeys(value ? [value] : [])}
            onPressEnter={() => confirm()}
            style={{ width: 150, marginBottom: 8, display: 'block' }}
            formatter={(value) =>
              value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''
            }
            parser={(value) => {
              if (!value) return 0;
              const parsed = parseFloat(value.replace(/\$\s?|(,*)/g, ''));
              return isNaN(parsed) ? 0 : parsed;
            }}
          />
          <div className="flex gap-1">
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 60 }}
            >
              Lọc
            </Button>
            <Button
              onClick={() => clearFilters?.()}
              size="small"
              style={{ width: 60 }}
            >
              Xóa
            </Button>
          </div>
        </div>
      ),
      filteredValue: filteredInfo.totalDepositAmount || null,
      onFilter: (value, record) => {
        if (!value) return true;
        return (record.totalDepositAmount || 0) === value;
      },
    },
    {
      title: 'Đã rút',
      key: 'totalWithdrawal',
      width: 130,
      render: (_, record) => {
        const amount = record.totalWithdrawalAmount || 0;
        return <span className="font-semibold ">{formatCurrency(amount)}</span>;
      },
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div className="p-2">
          <InputNumber
            placeholder="Nhập số tiền"
            value={selectedKeys[0] as number}
            onChange={(value) => setSelectedKeys(value ? [value] : [])}
            onPressEnter={() => confirm()}
            style={{ width: 150, marginBottom: 8, display: 'block' }}
            formatter={(value) =>
              value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''
            }
            parser={(value) => {
              if (!value) return 0;
              const parsed = parseFloat(value.replace(/\$\s?|(,*)/g, ''));
              return isNaN(parsed) ? 0 : parsed;
            }}
          />
          <div className="flex gap-1">
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 60 }}
            >
              Lọc
            </Button>
            <Button
              onClick={() => clearFilters?.()}
              size="small"
              style={{ width: 60 }}
            >
              Xóa
            </Button>
          </div>
        </div>
      ),
      filteredValue: filteredInfo.totalWithdrawalAmount || null,
      onFilter: (value, record) => {
        if (!value) return true;
        return (record.totalWithdrawalAmount || 0) === value;
      },
    },
    {
      title: 'Chênh lệch',
      key: 'balanceDifference',
      width: 140,
      render: (_, record) => {
        const amount = Math.abs(record.balanceDifference || 0);
        return <span className="font-semibold ">{formatCurrency(amount)}</span>;
      },
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div className="p-2">
          <InputNumber
            placeholder="Nhập số tiền"
            value={selectedKeys[0] as number}
            onChange={(value) => setSelectedKeys(value ? [value] : [])}
            onPressEnter={() => confirm()}
            style={{ width: 150, marginBottom: 8, display: 'block' }}
            formatter={(value) =>
              value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''
            }
            parser={(value) => {
              if (!value) return 0;
              const parsed = parseFloat(value.replace(/\$\s?|(,*)/g, ''));
              return isNaN(parsed) ? 0 : parsed;
            }}
          />
          <div className="flex gap-1">
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 60 }}
            >
              Lọc
            </Button>
            <Button
              onClick={() => clearFilters?.()}
              size="small"
              style={{ width: 60 }}
            >
              Xóa
            </Button>
          </div>
        </div>
      ),
      filteredValue: filteredInfo.balanceDifference || null,
      onFilter: (value, record) => {
        if (!value) return true;
        return Math.abs(record.balanceDifference || 0) === value;
      },
    },
    {
      title: 'Thanh toán',
      key: 'totalPayment',
      width: 140,
      render: (_, record) => {
        const amount = record.totalPaymentAmount || 0;
        return (
          <span className="font-semibold text-green-600">
            {formatCurrency(amount)}
          </span>
        );
      },
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div className="p-2">
          <InputNumber
            placeholder="Nhập số tiền"
            value={selectedKeys[0] as number}
            onChange={(value) => setSelectedKeys(value ? [value] : [])}
            onPressEnter={() => confirm()}
            style={{ width: 150, marginBottom: 8, display: 'block' }}
            formatter={(value) =>
              value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''
            }
            parser={(value) => {
              if (!value) return 0;
              const parsed = parseFloat(value.replace(/\$\s?|(,*)/g, ''));
              return isNaN(parsed) ? 0 : parsed;
            }}
          />
          <div className="flex gap-1">
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 60 }}
            >
              Lọc
            </Button>
            <Button
              onClick={() => clearFilters?.()}
              size="small"
              style={{ width: 60 }}
            >
              Xóa
            </Button>
          </div>
        </div>
      ),
      filteredValue: filteredInfo.totalPaymentAmount || null,
      onFilter: (value, record) => {
        if (!value) return true;
        return (record.totalPaymentAmount || 0) === value;
      },
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>
      ),
      filters: [
        { text: 'Chưa làm', value: 'PENDING' },
        { text: 'Quá hạn', value: 'OVERDUE' },
        { text: 'Đã nạp đáo', value: 'PAID' },
        { text: 'Đã hoàn thành', value: 'COMPLETED' },
        { text: 'Không làm', value: 'ABANDONED' },
      ],
      filteredValue: filteredInfo.status || null,
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Nhân viên phụ trách',
      dataIndex: 'employees',
      key: 'employees',
      width: 180,
      render: (employees: any[]) => (
        <div className="space-y-1">
          {employees?.map((employee, index) => (
            <Tag key={index} className="text-xs">
              {employeeNames[employee.id] || employee.name || 'Loading...'}
            </Tag>
          ))}
        </div>
      ),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div className="p-2" style={{ width: 200 }}>
          <Select
            mode="multiple"
            placeholder="Chọn nhân viên"
            value={selectedKeys as string[]}
            onChange={(value) => setSelectedKeys(value)}
            style={{ width: '100%', marginBottom: 8 }}
            options={uniqueEmployees}
            allowClear
          />
          <div className="flex gap-1">
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 60 }}
            >
              Lọc
            </Button>
            <Button
              onClick={() => clearFilters?.()}
              size="small"
              style={{ width: 60 }}
            >
              Xóa
            </Button>
          </div>
        </div>
      ),
      filteredValue: filteredInfo.employees || null,
      onFilter: (value, record) => {
        const selectedNames = Array.isArray(value) ? (value as string[]) : [];
        if (selectedNames.length === 0) return true;

        const recordEmployeeNames: string[] =
          record.employees?.map(
            (emp) => employeeNames[emp.id] || emp.name || ''
          ) || [];

        return selectedNames.some((selectedName) =>
          recordEmployeeNames.includes(selectedName)
        );
      },
    },
    {
      title: 'Người tạo',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 120,
      render: (createdBy: number) => {
        return employeeNames[createdBy] || 'Loading...';
      },
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div className="p-2" style={{ width: 180 }}>
          <Input.Search
            placeholder="Tìm người tạo"
            allowClear
            value={selectedKeys[0] as string}
            onChange={(e) => {
              setSelectedKeys(e.target.value ? [e.target.value] : []);
            }}
            onSearch={() => confirm()}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8 }}
          />
          <div className="flex gap-1">
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 60 }}
            >
              Lọc
            </Button>
            <Button
              onClick={() => clearFilters?.()}
              size="small"
              style={{ width: 60 }}
            >
              Xóa
            </Button>
          </div>
        </div>
      ),
      filteredValue: filteredInfo.createdBy || null,
      onFilter: (value, record) => {
        if (!value) return true;
        const creatorName = employeeNames[record.createdBy] || '';
        return creatorName
          .toLowerCase()
          .includes((value as string).toLowerCase());
      },
    },
    {
      title: 'Ghi chú',
      dataIndex: 'notes',
      key: 'notes',
      width: 200,
      ellipsis: true,
      render: (notes: string) => (
        <span className="text-sm text-gray-600">{notes || '-'}</span>
      ),
    },
    {
      title: 'Hành động',
      key: 'actions',
      width: 160,
      fixed: 'right',
      render: (_, record) => (
        <MaturityScheduleExpandedActions
          record={record}
          isRowExpanded={isRowExpanded(record.id)}
          onExpand={(expanded) => handleExpand(expanded, record)}
          onDeposit={() => handleDeposit(record)}
          onWithdrawal={() => handleWithdrawal(record)}
          onToggleCompletion={() => handleToggleCompletion(record)}
          onRecordPayment={() => handleRecordPayment(record)}
          onExportExcel={() => handleExportExcel(record)}
          onEdit={() => handleEdit(record)}
          onDelete={() => handleDelete(record)}
          isAdmin={isAdmin}
        />
      ),
    },
  ];

  // Sync selectedYear with props
  useEffect(() => {
    if (propsSelectedYear !== undefined && propsSelectedYear !== selectedYear) {
      setSelectedYear(propsSelectedYear);
    }
  }, [propsSelectedYear]);

  // Handle year change
  const handleYearChange = (year: dayjs.Dayjs | null) => {
    if (year) {
      const newYear = year.year();
      setSelectedYear(newYear);
      if (onYearChange) {
        onYearChange(newYear);
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* Header with year picker */}
      <Card size="small">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <CalendarOutlined className="text-blue-500" />
            <span className="text-sm font-medium">Chọn năm:</span>
            <DatePicker
              picker="year"
              value={dayjs().year(selectedYear)}
              onChange={handleYearChange}
              format="YYYY"
              allowClear={false}
              className="w-32"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={loadSchedules}
              loading={loading}
              size="small"
            >
              Tải lại
            </Button>
          </div>
        </div>
      </Card>

      {/* Schedules Table */}
      <Card>
        <div className="mb-3 flex justify-end">
          <Button
            icon={<ReloadOutlined />}
            onClick={handleClearAllFilters}
            type="text"
            size="small"
            className="text-gray-500 hover:text-blue-600 hover:bg-blue-50 border-0 shadow-none transition-all duration-200 flex items-center gap-1"
            title="Xóa tất cả bộ lọc"
          >
            <span className="text-xs">Xoá bộ lọc</span>
          </Button>
        </div>
        <Spin spinning={loading}>
          <Table
            key={tableKey}
            columns={columns}
            dataSource={schedules.sort((a, b) => b.id - a.id)}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} của ${total} kỳ đáo hạn`,
            }}
            onChange={(pagination, filters, sorter) => {
              setFilteredInfo(filters);
            }}
            scroll={{ x: 2100 }}
            className="bg-white rounded-lg"
            size="middle"
            rowClassName="group hover:bg-gray-50"
            locale={{
              emptyText: `Không có kỳ đáo hạn nào trong năm ${selectedYear}`,
            }}
            expandable={{
              expandedRowRender: (record) => (
                <TransactionSubTable
                  maturityScheduleId={record.id}
                  onTransactionUpdate={handleModalSuccess}
                  refreshTrigger={transactionRefreshTrigger}
                  isAdmin={isAdmin}
                />
              ),
              rowExpandable: () => true,
              expandRowByClick: false,
              expandedRowKeys: expandedRowKeys,
              onExpand: handleExpand,
              showExpandColumn: false,
            }}
          />
        </Spin>
      </Card>

      {/* Multi Export Modal */}
      <CardMultiExportModal
        visible={showMultiExportModal}
        onClose={() => setShowMultiExportModal(false)}
        maturitySchedules={schedules}
        cardInfo={
          cardInfo || {
            cardNumber: '',
            cardHolderName: '',
            customerName: '',
            bankName: '',
            cardType: '',
          }
        }
        selectedYear={selectedYear}
      />

      {/* Existing Modals */}
      {selectedRecord && (
        <>
          <DepositModal
            visible={depositModalVisible}
            onClose={() => handleModalClose(setDepositModalVisible)}
            onSuccess={handleModalSuccess}
            maturitySchedule={selectedRecord}
          />

          <WithdrawalModal
            visible={withdrawalModalVisible}
            onClose={() => handleModalClose(setWithdrawalModalVisible)}
            onSuccess={handleModalSuccess}
            maturitySchedule={selectedRecord}
          />

          <EditMaturityScheduleModal
            visible={editModalVisible}
            onClose={() => handleModalClose(setEditModalVisible)}
            onSuccess={handleModalSuccess}
            maturitySchedule={selectedRecord}
            employeeId={employee?.id || 0}
          />

          <MaturityPaymentModal
            visible={paymentModalVisible}
            onCancel={() => handleModalClose(setPaymentModalVisible)}
            onSuccess={handleModalSuccess}
            maturitySchedule={selectedRecord}
          />
        </>
      )}
    </div>
  );
};

export default MaturityScheduleList;
